import type {
  CustomerIdentity,
  CustomerProductCatalog,
  InquiryResultResponse,
  QueryOrder,
  WalletSummary,
} from "../types/api";


const PRODUCT_NAMES: Partial<Record<QueryOrder["productCode"], string>> = {
  NATURAL_PERSON: "自然人综合背景协查",
  ENTERPRISE: "企业主体协查",
  ENTERPRISE_CREDIT: "企业信用全景查询",
  ENTERPRISE_OPERATION: "企业经营信息查询",
  OWNED_VEHICLE: "名下车辆协查",
  VEHICLE_DETAIL: "指定车辆信息协查",
  PROPERTY: "个人不动产信息查询",
  PROPERTY_ENTERPRISE: "企业不动产信息查询",
  VERIFICATION: "信息核验",
};

const STATUS_PRESENTATION: Record<string, { status: string; tone: string }> = {
  COMPLETED: { status: "已完成", tone: "success" },
  PARTIAL_SUCCESS: { status: "处理中", tone: "waiting" },
  QUERYING: { status: "查询中", tone: "waiting" },
  PAID: { status: "待查询", tone: "waiting" },
  PENDING_MANUAL: { status: "待处理", tone: "waiting" },
  PENDING_PAYMENT: { status: "待支付", tone: "waiting" },
  CANCELED: { status: "已关闭", tone: "muted" },
  REFUNDED: { status: "已退款", tone: "muted" },
};

const BILLING_POLICY_PRESENTATION: Record<NonNullable<QueryOrder["billingPolicyCode"]>, string> = {
  QUERY_SUCCEEDED: "查询收费",
  HIT_SUCCEEDED: "查得收费",
  REPORT_DELIVERED: "报告交付收费",
};

const BILLING_STATUS_PRESENTATION: Record<NonNullable<QueryOrder["billingStatus"]>, { billingResult: string; billingTone: string }> = {
  NOT_RESERVED: { billingResult: "待结算", billingTone: "waiting" },
  RESERVED: { billingResult: "待结算", billingTone: "waiting" },
  SETTLED: { billingResult: "已计费", billingTone: "success" },
  RELEASED: { billingResult: "未计费", billingTone: "muted" },
};

const INQUIRY_PARAM_LABELS: ReadonlyArray<[
  keyof NonNullable<QueryOrder["inquiryPayload"]>,
  string,
]> = [
  ["name", "姓名"],
  ["id_card", "身份证号"],
  ["cell", "手机号"],
  ["marriage_auth_code", "婚姻授权码"],
  ["plate", "车牌号"],
  ["plate_type", "车辆类型"],
  ["owner_name", "所有人姓名"],
  ["company_name", "企业名称"],
  ["credit_code", "统一社会信用代码"],
  ["legal_representative", "法定代表人"],
];

function inquiryParams(order: QueryOrder): Array<[string, string]> {
  const params = INQUIRY_PARAM_LABELS.flatMap(([key, label]) => {
    const value = order.inquiryPayload?.[key]?.trim();
    return value ? [[label, value] as [string, string]] : [];
  });
  return params.length ? params : [["查询内容", order.subjectSummary]];
}

function formatCreatedAt(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
}

export function buildOrderContextMessage(orders: QueryOrder[]) {
  return {
    type: "judicial:orders-context" as const,
    orders: orders.map((order) => {
      const presentation = STATUS_PRESENTATION[order.status] ?? {
        status: "处理中",
        tone: "waiting",
      };
      return {
        id: order.id,
        productCode: order.productCode,
        time: formatCreatedAt(order.createdAt),
        target: order.subjectSummary,
        params: inquiryParams(order),
        product: PRODUCT_NAMES[order.productCode] ?? "协查服务",
        fee: order.salePrice,
        billingMethod: order.billingPolicyCode
          ? BILLING_POLICY_PRESENTATION[order.billingPolicyCode]
          : "—",
        ...(order.billingStatus
          ? BILLING_STATUS_PRESENTATION[order.billingStatus]
          : { billingResult: "—", billingTone: "muted" }),
        ...presentation,
        canView: [
          "NATURAL_PERSON",
          "ENTERPRISE_OPERATION",
          "OWNED_VEHICLE",
          "VEHICLE_DETAIL",
          "PROPERTY",
          "PROPERTY_ENTERPRISE",
        ].includes(order.productCode)
          && order.status === "COMPLETED",
        ...(order.reportExport ? { reportExport: order.reportExport } : {}),
      };
    }),
  };
}

export function buildProductContextMessage(
  catalog: CustomerProductCatalog,
  authorizationMaterialRequired: boolean,
  organizationName: string,
) {
  return {
    type: "judicial:products-context" as const,
    authorizationMaterialRequired,
    organizationName,
    products: catalog.modules.flatMap((module) => module.products),
  };
}

export function buildLawFirmAccountContext(
  identity: CustomerIdentity,
  wallet: WalletSummary,
) {
  const isAdministrator = identity.role === "ORGANIZATION_ADMIN";
  return {
    type: "judicial:account-context" as const,
    organizationName: identity.organization_name,
    accountDisplayName: identity.is_personal_lawyer
      ? `${identity.account_name ?? "律师"}律师 · 个人账号`
      : identity.organization_name,
    isPersonalLawyer: identity.is_personal_lawyer === true,
    balanceLabel: isAdministrator ? "当前余额" : "可用额度",
    availableBalance: isAdministrator ? wallet.firm_balance : wallet.lawyer_quota,
  };
}


export async function resolveResultRequest(
  message: unknown,
  loadResult: (orderId: string) => Promise<InquiryResultResponse>,
) {
  if (
    !message
    || typeof message !== "object"
    || !("type" in message)
    || !("orderId" in message)
    || message.type !== "judicial:open-order-result"
    || typeof message.orderId !== "string"
  ) {
    return null;
  }
  const result = await loadResult(message.orderId);
  return { type: "judicial:order-result" as const, result };
}
